import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressBar } from 'primereact/progressbar';

/**
 * Unified self‑contained photo manager.
 *
 * Props
 * ────────────────────────────────────────────────
 * photos: Array<{ url: string; tag: string; type: string }>
 * editable: boolean                     // edit/view mode flag
 * onAddPhoto:  (file: File, tag: string, type: string) => void
 * onRemovePhoto: (index: number) => void
 */
const PhotoManager = ({
  photos = [],
  editable,
  onAddPhoto,
  onRemovePhoto,
  autoSave = false,        // true => call onAutoSave after batch
  onAutoSave,              // usually OrderViewEdit.handleSave
  canUpload = true         // false => disable dropzone + button (no customer name)
}) => {
  /* ─── Drag‑&‑Drop Upload ─────────────────────── */
  const [pendingUploads, setPendingUploads] = useState([]); // [{file,tag,type}]
  const anyUploading = pendingUploads.some(p => p.uploading);

  const allTagged = pendingUploads.length > 0 && pendingUploads.every(u => u.tag && u.type && !u.uploading);

  const handleUploadAll = async () => {
    setPendingUploads(prev => prev.map(i => ({ ...i, uploading: true, progress: 0, error: null })));

    const added = []; // gather successfully-added photos returned by onAddPhoto
    let successCount = 0;

    for (let idx = 0; idx < pendingUploads.length; idx++) {
      const entry = pendingUploads[idx];
      try {
        const newPhoto = await onAddPhoto(entry.file, entry.tag, entry.type, (percent) => {
          updatePending(idx, { progress: percent });
        });
        if (newPhoto) added.push(newPhoto);
        successCount++;
        updatePending(idx, { uploading: false, progress: 100, error: null });
      } catch (err) {
        updatePending(idx, { uploading: false, error: err?.message || "Upload failed" });
      }
    }

    setPendingUploads(prev => prev.filter(item => item.error));

    const failCount = pendingUploads.length - successCount;
    toast.current?.show({
      severity: failCount ? "warn" : "success",
      summary: "Upload finished",
      detail: `${successCount} succeeded${failCount ? `, ${failCount} failed` : ''}.`
    });

    // one save after the batch
    if (successCount > 0 && autoSave && typeof onAutoSave === 'function') {
      await onAutoSave(added);
    }
  };




  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(f => {
      // console.log("Dropped file:", f.name, "type:", f.type, "size:", f.size);
    });
    const mapped = acceptedFiles.map((file) => ({
      file,
      tag: "",
      type: "",
      uploading: false,
      progress: 0,
      error: null
    }));
    setPendingUploads((prev) => [...prev, ...mapped]);
  }, []);

  const updatePending = (idx, changes) => {
    setPendingUploads(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...changes };
      return copy;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"]
    },
    disabled: !editable || anyUploading || !canUpload
  });


  /* ─── Preview / Navigation Dialog ────────────── */
  const [previewIdx, setPreviewIdx] = useState(null); // null = closed
  const openPreview = (idx) => setPreviewIdx(idx);
  const closePreview = () => setPreviewIdx(null);
  const navigate = (dir) => {
    if (previewIdx === null) return;
    setPreviewIdx((prev) => {
      let next = prev + dir;
      if (next < 0) next = photos.length - 1;
      if (next >= photos.length) next = 0;
      return next;
    });
  };

  /* ─── Toast ──────────────────────────────────── */
  const toast = useRef(null);
  const warn = (summary, detail) => {
    toast.current?.show({ severity: "warn", summary, detail });
  };

  /* ─── Static select options ──────────────────── */
  const TAG_OPTIONS = [
    { label: "Inspiration", value: "Inspiration" },
    { label: "Sketches", value: "Sketches" },
    { label: "Finished Product", value: "Finished Product" }
  ];
  const TYPE_OPTIONS = [
    { label: "Cake", value: "cake" },
    { label: "Cookies", value: "cookies" },
    { label: "Cupcakes", value: "cupcakes" },
    { label: "Cake Pops", value: "cakepops" },
    { label: "General", value: "general" }
  ];

  /* ─── Helpers ────────────────────────────────── */
  const handleUploadOne = async (idx) => {
    const entry = pendingUploads[idx];
    if (!entry.tag || !entry.type) {
      warn("Missing info", "Please choose Tag & Type before uploading.");
      return;
    }

    // mark uploading
    updatePending(idx, { uploading: true, progress: 0, error: null });

    try {
      // If onAddPhoto can accept progress callback, e.g.:
      await onAddPhoto(entry.file, entry.tag, entry.type, (percent) => {
        updatePending(idx, { progress: percent });
      });

      // Success: remove from pending
      setPendingUploads((prev) => prev.filter((_, i) => i !== idx));
      toast.current?.show({
        severity: "success",
        summary: "Uploaded",
        detail: entry.file.name
      });
    } catch (err) {
      updatePending(idx, { uploading: false, error: err.message || "Upload failed" });
      toast.current?.show({
        severity: "error",
        summary: "Upload failed",
        detail: err.message || "Check file type/size."
      });
    }
  };


  const confirmDelete = (idx) => {
    if (window.confirm("Are you sure you want to delete this photo?")) {
      onRemovePhoto(idx);
    }
  };

  const downloadPhoto = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ─── Render ─────────────────────────────────── */
  return (
    <div className="photo-manager">
      <Toast ref={toast} />

      {/* Upload area */}
      {editable && (
        <div
          {...getRootProps({ className: "photo-dropzone" })}
          style={{
            border: "2px dashed var(--surface-border)",
            padding: "1.5rem",
            borderRadius: "6px",
            textAlign: "center",
            marginBottom: "1rem",
            cursor: anyUploading ? "not-allowed" : "pointer",
            opacity: anyUploading ? 0.6 : 1,
            background: isDragActive ? "var(--highlight-bg)" : "inherit"
          }}
        >
           <input {...getInputProps()} disabled={anyUploading || !canUpload} />
          {!canUpload ? (
            <p>Enter a customer name to enable uploads</p>
          ) : isDragActive ? (
            <p>Drop files here…</p>
          ) : (
            <p>Drag & drop images here, or click to select</p>
          )}
        </div>
      )}

      {/* Pending uploads list */}
      {editable && pendingUploads.length > 0 && (
        <div className="pending-uploads">
          <h4>Pending Uploads</h4>
          {pendingUploads.map((item, idx) => (
            <div
              key={idx}
              className="pending-item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                marginBottom: "0.75rem",
                border: "1px solid var(--surface-border)",
                padding: "0.75rem",
                borderRadius: "6px",
                position: "relative",
                background: "#fff"
              }}
            >
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                <div style={{ flex: 2, fontWeight: 500 }}>{item.file.name}</div>
                <div style={{ flex: 1 }}>
                  <Dropdown
                    value={item.tag}
                    options={TAG_OPTIONS}
                    onChange={(e) => {
                      const t = e.value;
                      setPendingUploads((prev) => {
                        const n = [...prev];
                        n[idx] = { ...n[idx], tag: t };
                        return n;
                      });
                    }}
                    placeholder="Tag"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Dropdown
                    value={item.type}
                    options={TYPE_OPTIONS}
                    onChange={(e) => {
                      const t = e.value;
                      setPendingUploads((prev) => {
                        const n = [...prev];
                        n[idx] = { ...n[idx], type: t };
                        return n;
                      });
                    }}
                    placeholder="Type"
                  />
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {/* <Button
                    icon="pi pi-check"
                    className="p-button-success p-button-sm"
                    disabled={!item.tag || !item.type || item.uploading}
                    onClick={() => handleUploadOne(idx)}
                  /> */}
                  <Button
                    icon="pi pi-times"
                    className="p-button-secondary p-button-text p-button-sm"
                    onClick={() => setPendingUploads((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={item.uploading}
                  />
                </div>
              </div>

              {/* Upload progress / error */}
               {item.uploading && <ProgressBar value={item.progress} showValue={false} />}
              {item.error && (
                <div style={{ color: "#c62828", fontSize: "0.85rem" }}>
                  Error: {item.error}
                </div>
              )}
            </div>
          ))}

           <Button
              label="Upload All"
              icon="pi pi-cloud-upload"
              className="p-button-success"
              disabled={!allTagged || anyUploading || !canUpload}
              onClick={handleUploadAll}
              style={{ marginTop: 12 }}
            />


        </div>
      )}

      {/* Existing photos grid */}
      {photos && photos.length > 0 ? (
        <div className="photos-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: "0.75rem" }}>
          {photos.map((photo, idx) => (
            <div key={idx} className="photo-card" style={{ position: "relative" }}>
              <img
                src={photo.url}
                alt={`photo-${idx}`}
                style={{ width: "100%", borderRadius: "4px", cursor: "pointer" }}
                onClick={() => openPreview(idx)}
              />
              <div style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>
                <strong>{photo.tag}</strong> | {photo.type}
              </div>
              {/* Download */}
              <Button
                icon="pi pi-download"
                className="p-button-text p-button-sm"
                style={{ position: "absolute", top: "4px", right: editable ? "34px" : "4px" }}
                onClick={() => downloadPhoto(photo.url)}
              />
              {/* Delete */}
              {editable && (
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  style={{ position: "absolute", top: "4px", right: "4px" }}
                  onClick={() => confirmDelete(idx)}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p>No photos uploaded.</p>
      )}

      {/* Preview dialog */}
      <Dialog
        visible={previewIdx !== null}
        onHide={closePreview}
        header={`Photo ${previewIdx !== null ? previewIdx + 1 : ""}`}
        style={{ width: "50vw" }}
        footer={previewIdx !== null && (
          <div>
            <Button icon="pi pi-arrow-left" label="Prev" className="p-button-text" onClick={() => navigate(-1)} />
            <Button icon="pi pi-arrow-right" label="Next" className="p-button-text" onClick={() => navigate(1)} />
          </div>
        )}
      >
        {previewIdx !== null && (
          <div style={{ textAlign: "center" }}>
            <img src={photos[previewIdx].url} alt="full" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default PhotoManager;
